<template>
    <div>
        <data-iterator
            ref="iterator"
            :api="api"
            :filters.sync="filters"
            :fixed-filters="{ company: { exact: company } }"
        >
            <template v-slot:actions>
                <slot name="actions"></slot>
            </template>

            <template slot-scope="{ items }">
                <d-table
                    :items="items"
                    :headers="headers"
                    :filters.sync="filters"
                    row-select
                    row-action
                    @onRowSelected="onRowSelected"
                >
                    <!-- name column -->
                    <template v-slot:[`item.name`]="{ item }">
                        <router-link :to="getEditLink(item)">
                            {{ item.name }}
                        </router-link>
                    </template>

                    <!-- days column -->
                    <template v-slot:[`item.intervals`]="{ item }">
                        <v-chip
                            v-for="day in item.days"
                            :key="day.day"
                            small
                            class="mr-1"
                        >
                            {{ $t('days_of_week.' + (day.day - 1)) }}
                        </v-chip>
                    </template>

                    <!-- interval column -->
                    <template v-slot:[`item.intervalTimeUnit`]="{ item }">
                        <span v-if="item.intervalTimePiece % 7 == 0">
                            {{
                                $tc(
                                    'labels.interval_week',
                                    item.intervalTimePiece / 7,
                                    { n: item.intervalTimePiece / 7 }
                                )
                            }}
                        </span>
                        <span v-else>
                            {{
                                $tc(
                                    'labels.interval_' + item.intervalTimeUnit,
                                    item.intervalTimePiece,
                                    { n: item.intervalTimePiece }
                                )
                            }}
                        </span>
                    </template>

                    <!-- budget column -->
                    <template v-slot:[`item.budgetLimit`]="{ item }">
                        {{ item.budgetLimit | currency }}
                    </template>

                    <!-- active -->
                    <template v-slot:[`item.active`]="{ item }">
                        <checkbox-input
                            v-if="showActivateCheckboxes"
                            :value="item.isActive"
                            @input="
                                item.isActive
                                    ? confirmDeactivation(item)
                                    : confirmActivation(item)
                            "
                        ></checkbox-input>
                    </template>

                    <!-- row actions -->
                    <template v-slot:rowActions="{ item }">
                        <edit-button :to="getEditLink(item)"></edit-button>
                        <delete-action
                            :entity-name="item.name"
                            :entity-id="item.id"
                            :api="api"
                            @deleted="onEntityDeleted"
                            @failed="onEntityDeleteFailed"
                        ></delete-action>
                    </template>
                </d-table>
            </template>

            <!-- batch actions -->
            <template v-slot:batchActions>
                <delete-batch-action
                    :selected-ids="selectedIds"
                    :api="api"
                    @finished="onBatchDeleteFinished"
                    @finishedWithErrors="onBatchDeleteFinishedWithErrors"
                    @canceled="onBatchDeleteCanceled"
                ></delete-batch-action>
            </template>
        </data-iterator>

        <confirm-modal
            :value="confirmActivationModal"
            :text="
                $t('texts.confirm_allowance_activation', {
                    restaurant: itemSelectedForActivation
                        ? itemSelectedForActivation.name
                        : '',
                })
            "
            :processing="processingActivation"
            @onApprove="activateEntity"
            @onCancel="resetActiveCheckboxes"
        ></confirm-modal>
        <confirm-modal
            :value="confirmDeactivationModal"
            :text="
                $t('texts.confirm_allowance_deactivation', {
                    restaurant: itemSelectedForActivation
                        ? itemSelectedForActivation.name
                        : '',
                })
            "
            :processing="processingActivation"
            @onApprove="deactivateEntity"
            @onCancel="resetActiveCheckboxes"
        ></confirm-modal>
    </div>
</template>

<script>
import routeType from '@/router/routeType'
import RestApiType from '@/api/RestApiType'
import RestApiCollection from '@/api/RestApiCollection'
import DataIteratorMixin from '@/components/mixins/DataIteratorMixin'
import DataTableMixin from '@/components/mixins/DataTableMixin'
import ActivateMixin from '@/components/mixins/ActivateMixin'
import CheckboxInput from '@/components/form/Inputs/CheckboxInput'
import ConfirmModal from '@/components/confirm/ConfirmModal'
import AllowancesHeaders from '@/pages/companies/mixins/AllowancesHeaders'

export default {
    components: { CheckboxInput, ConfirmModal },
    mixins: [
        DataIteratorMixin,
        DataTableMixin,
        ActivateMixin,
        AllowancesHeaders,
    ],
    props: {
        companyId: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            api: RestApiType.COMPANY_ALLOWANCES,
            notiLabel: 'labels.allowance',
        }
    },
    computed: {
        restApi() {
            return RestApiCollection.get(this.api)
        },

        company() {
            return this.restApi.getItemIri(this.companyId)
        },
    },
    methods: {
        getEditLink(item) {
            return {
                name: routeType.COMPANY_ALLOWANCE_EDIT,
                params: { companyId: this.companyId, allowanceId: item.id },
                query: { tab: 0 },
            }
        },
    },
}
</script>
